import {
  type CSSProperties,
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

export interface UseScrollFadeResult {
  maskStyle: CSSProperties | undefined;
  suppressBottomFade: (durationMs: number) => void;
}

const SCROLLED_THRESHOLD_PX = 10;

type VirtualScrollBounds = {
  readonly scrollOffset: number;
  readonly scrollSize: number;
  readonly viewportSize: number;
};

type ScrollFadeForOffsetOptions = {
  fadeSize?: number;
  bottomFade?: string;
  scrollRef?: RefObject<VirtualScrollBounds | null>;
};

export function useScrollFade(
  ref: RefObject<HTMLElement | null>,
  options: { fadeSize?: number; enabled?: boolean } = {}
): UseScrollFadeResult {
  const { fadeSize = 4, enabled = true } = options;
  const [fadeTop, setFadeTop] = useState(false);
  const [fadeBottom, setFadeBottom] = useState(false);
  const suppressRef = useRef(false);
  const updateRef = useRef<() => void>(() => {});
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const resetFade = () => {
      setFadeTop(false);
      setFadeBottom(false);
    };

    if (!enabled) {
      resetFade();
      return;
    }

    const el = ref.current;
    if (!el) {
      resetFade();
      return;
    }

    const update = () => {
      const nextTop = el.scrollTop > 0;
      const nextBottom =
        !suppressRef.current &&
        el.scrollTop + el.clientHeight < el.scrollHeight - 1;
      setFadeTop((prev) => (prev === nextTop ? prev : nextTop));
      setFadeBottom((prev) => (prev === nextBottom ? prev : nextBottom));
    };

    updateRef.current = update;
    update();

    const observer = new ResizeObserver(update);
    observer.observe(el);
    el.addEventListener('scroll', update, { passive: true });

    return () => {
      observer.disconnect();
      el.removeEventListener('scroll', update);
      updateRef.current = () => {};

      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      suppressRef.current = false;
    };
  }, [ref, enabled]);

  useEffect(
    () => () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    },
    []
  );

  const suppressBottomFade = useCallback((durationMs: number) => {
    suppressRef.current = true;
    setFadeBottom(false);

    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(() => {
      suppressRef.current = false;
      timerRef.current = null;
      updateRef.current();
    }, durationMs);
  }, []);

  const maskStyle = buildMaskStyle(
    fadeTop ? topFadeStop(`${fadeSize}px`) : NO_TOP_FADE,
    fadeBottom ? bottomFadeStop(`${fadeSize}px`) : NO_BOTTOM_FADE
  );

  return { maskStyle, suppressBottomFade };
}

export function useScrollFadeForOffset(
  containerRef: RefObject<HTMLElement | null>,
  { fadeSize = 40, bottomFade, scrollRef }: ScrollFadeForOffsetOptions = {}
) {
  const onScrollOffset = useCallback(
    (offset: number) => {
      const element = containerRef.current;
      if (!element) return;

      const bounds = scrollRef?.current;
      const hasOverflow = bounds && bounds.scrollSize > bounds.viewportSize + 1;
      const fadeTop = scrollRef
        ? Boolean(hasOverflow && offset > 0)
        : offset > SCROLLED_THRESHOLD_PX;
      const fadeBottom = scrollRef
        ? Boolean(
            bounds && offset + bounds.viewportSize < bounds.scrollSize - 1
          )
        : Boolean(bottomFade);

      setMask(
        element,
        fadeTop ? topFadeStop(`${fadeSize}px`) : NO_TOP_FADE,
        fadeBottom
          ? bottomFadeStop(bottomFade ?? `${fadeSize}px`)
          : NO_BOTTOM_FADE
      );
    },
    [containerRef, fadeSize, bottomFade, scrollRef]
  );

  useLayoutEffect(() => {
    if (!scrollRef) {
      onScrollOffset(0);
      return;
    }

    const container = containerRef.current;
    if (!container) return;

    const updateMask = () =>
      onScrollOffset(scrollRef.current?.scrollOffset ?? 0);
    const resizeObserver = new ResizeObserver(updateMask);
    const observeList = () => {
      resizeObserver.disconnect();
      resizeObserver.observe(container);

      // VList owns the viewport and its inner spacer tracks virtual content size
      const viewport = container.firstElementChild;
      if (viewport) {
        resizeObserver.observe(viewport);
        if (viewport.firstElementChild)
          resizeObserver.observe(viewport.firstElementChild);
      }

      updateMask();
    };

    observeList();
    const childrenObserver = new MutationObserver(observeList);
    childrenObserver.observe(container, { childList: true });

    return () => {
      resizeObserver.disconnect();
      childrenObserver.disconnect();
    };
  }, [containerRef, onScrollOffset, scrollRef]);

  return onScrollOffset;
}

const NO_TOP_FADE = 'black 0px';
const NO_BOTTOM_FADE = 'black 100%';

const topFadeStop = (fade: string) => `transparent, black ${fade}`;
const bottomFadeStop = (fade: string) =>
  `black calc(100% - ${fade}), transparent`;

function buildMaskStyle(
  top: string,
  bottom: string
): CSSProperties | undefined {
  if (top === NO_TOP_FADE && bottom === NO_BOTTOM_FADE) return undefined;
  const mask = `linear-gradient(to bottom, ${top}, ${bottom})`;
  return { WebkitMaskImage: mask, maskImage: mask };
}

function setMask(element: HTMLElement, top: string, bottom: string) {
  const style = buildMaskStyle(top, bottom);
  if (!style) {
    element.style.removeProperty('mask-image');
    element.style.removeProperty('-webkit-mask-image');
    return;
  }
  element.style.setProperty('mask-image', style.maskImage as string);
  element.style.setProperty('-webkit-mask-image', style.maskImage as string);
}
